import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import {
  SecureIcon,
  EducationIcon,
  FinanceIcon,
  EquitableIcon,
  SustainableIcon,
  InnovationIcon,
  RegulationIcon,
  InfrastructureIcon,
  ChevronRight,
  Info,
} from '../icons';
import Loading from '../common_components/Loading';

const rankingMapping = {
  statistical: {
    international: InternationalStatisticalRanking,
    domestic: PolandStatisticalRanking,
  },
  custom: {
    international: InternationalUserRanking,
    domestic: PolandUserRanking,
  },
};

const INDICATORS_INFO = {
  equitable: {
    label: 'Sprawiedliwość',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Stopień elektryfikacji w miastach, Stopień elektryfikacji na wsiach, Dostęp do czystych paliw do celów kulinarnych, Cena energii elektrycznej dla gospodarstw domowych mierzona parytetem siły nabywczej, Cena energii elektrycznej dla przemysłu, Cena hurtowa gazu, Subsydiowanie energii, Niewyceniony koszt efektów zewnętrznych, Udział importu paliw w PKB.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Średnia cena energii dla gospodarstw domowych w relacji do przeciętnego wynagrodzenia w gospodarce narodowej, Średnia cena energii dla przemysłu w relacji do przeciętnego wynagrodzenia w gospodarce narodowej, Średnia cena gazu RDB w relacji do przeciętnego wynagrodzenia w gospodarce narodowej, Subsydiowanie energii, Niewyceniony koszt efektów zewnętrznych, Udział importu paliw w PKB.',
  },
  secure: {
    label: 'Bezpieczeństwo',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Dywersyfikacja podaży pierwotnych źródeł energii, Import energii netto, Dywersyfikacja generacji energii elektrycznej, Elastyczność systemu energetycznego, Odporność systemu dostaw gazu, Wskaźnik długości przerw w dostawach energii, Wskaźnik częstotliwości przerw w dostawach energii, Wskaźnik utraty mocy.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Dywersyfikacja podaży pierwotnych źródeł energii, Import energii netto, Dywersyfikacja generacji energii elektrycznej, Elastyczność systemu energetycznego, Odporność systemu dostaw gazu, Wskaźnik długości przerw w dostawach energii, Wskaźnik częstotliwości przerw w dostawach energii, Wskaźnik utraty mocy.',
  },
  sustainable: {
    label: 'Zrównoważony rozwój',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Energochłonność, Zużycie energii na mieszkańca, Emisja CO2 na mieszkańca, Emisja CO2 w procesie zaopatrzenia w energię, Intensywność emisji CH4 w procesie produkcji energii, Udział czystej energii w końcowym zużyciu energii.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Energochłonność w relacji do przeciętnego wynagrodzenia w gospodarce narodowej, Zużycie energii na mieszkańca, Emisja CO2 na mieszkańca, Emisja CO2 w procesie zaopatrzenia w energię, Intensywność emisji CH4 w procesie produkcji energii, Udział czystej energii w końcowym zużyciu energii.',
  },
  regulation: {
    label: 'Regulacja i system polityczny',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: RISE - podwskaźnik dostępności energii, RISE - podwskaźnik efektywności energetycznej, RISE - podwskaźnik wykorzystania zielonej energii do zaspokajania potrzeb kulinarnych, Wskaźnik zaangażowania w ideę Net Zero, Stabilność polityczna, Wskaźnik efektywnej dekarbonizacji.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Poparcie dla energetyki jądrowej (sondaż CBOS), RISE - podwskaźnik dostępności energii, RISE - podwskaźnik efektywności energetycznej, Stabilność polityczna, Wskaźnik efektywnej dekarbonizacji.',
  },
  infrastructure: {
    label: 'Infrastruktura',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Wskaźnik budowania potencjału odnawialnych źródeł energii, Jakość sieci transportowej, Gotowość do cyfryzacji.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Wskaźnik budowania potencjału odnawialnych źródeł energii, Jakość sieci transportowej, Gotowość do cyfryzacji.',
  },
  education: {
    label: 'Edukacja i kapitał ludzki',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Odsetek zatrudnionych w sektorach związanych z energetyką odnawialną, Jakość edukacji, Zasoby wysokokwalifikowanej kadry.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Odsetek zatrudnionych w sektorach związanych z energetyką odnawialną, Jakość edukacji, Zasoby wysokokwalifikowanej kadry.',
  },
  innovation: {
    label: 'Innowacje',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Innowacyjne środowisko biznesowe, Publiczne nakłady na B&R, Tempo rozwoju technologii związanych z ochroną środowiska.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Innowacyjne środowisko biznesowe, Publiczne nakłady na B&R, Tempo rozwoju technologii związanych z ochroną środowiska.',
  },
  finance: {
    label: 'Finanse i inwestycje',
    descriptionZite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Rating kredytowy, Wielkość krajowych zobowiązań kredytowych, Poziom regulacji dla FDI, Rządy prawa, Międzynarodowe wsparcie finansowe, Inwestycje w OZE.',
    descriptionPite:
      'Kategoria ta obejmuje następujące wskaźniki składowe: Zaufanie do rządu (sondaż CBOS), Indeks wolności gospodarczej Heritage Foundation, Wielkość krajowych zobowiązań kredytowych, Rządy prawa, Międzynarodowe wsparcie finansowe, Inwestycje w OZE.',
  },
};

const CHART_LEGEND_ITEMS = [
  {
    colorClass: 'bg-secondary',
    label: 'Państwa przed Polską',
  },
  {
    colorClass: 'bg-info',
    label: 'Polska',
  },
  {
    colorClass: 'bg-primary',
    label: 'Państwa tuż za Polską',
  },
];

export default function App({
  internationalRankingYearsAvailable,
  urls,
  csrfToken,
}) {
  const [chosenRanking, setChosenRanking] = useState({
    mode: 'statistical', // statistical / custom
    scope: 'international', // international / domestic
  });
  const [indicators, setIndicators] = useState({
    arbitrary_assessment: 3,
    equitable: 3,
    secure: 3,
    sustainable: 3,
    regulation: 3,
    infrastructure: 3,
    education: 3,
    innovation: 3,
    finance: 3,
  });

  const RankingComponent =
    rankingMapping[chosenRanking.mode][chosenRanking.scope];

  return (
    <div className="prose w-full max-w-[--page-content-w] text-left sm:text-justify">
      <h1 className="primary-header h1-as-h2 relative">
        Indeks Transformacji Energetycznej (ITE)
        <TutorialBtn />
      </h1>
      <TutorialBtn
        className={
          'inline-flex lg:hidden text-black shadow-primary static ml-6'
        }
      />
      {/* This is the same description as in "about_project.html" */}
      <p>
        Celem narzędzia Indeks Transformacji Energetycznej (ITE) jest
        umożliwienie użytkownikom wyznaczenia dwóch nowych wariantów indeksu
        transformacji energetycznej:
      </p>
      <ol>
        <li>
          <strong>Wariantu międzynarodowego</strong>, stanowiącego modyfikację
          indeksu ETI (tzw. Zmodyfikowany Indeks Transformacji Energetycznej, w
          skrócie ZITE) publikowanego przez Światowe Forum Ekonomiczne. Wariant
          ten opiera się na zaawansowanej metodologii statystycznej
          rekomendowanej przez OECD oraz Wspólne Centrum Badawcze (JRC) Komisji
          Europejskiej, co pozwala ograniczyć wpływ subiektywnych decyzji na
          konstrukcję wskaźnika kompozytowego. Wspomniany algorytm obliczeniowy
          bazuje na{' '}
          <a
            href="https://journals.agh.edu.pl/er/article/view/4750/2720"
            target="_blank"
            rel="noopener noreferrer"
          >
            tym artykule
          </a>
          .
        </li>
        <li>
          <strong>Polskiego Indeksu Transformacji Energetycznej (PITE)</strong>,
          który – oprócz zastosowania odpornej metodologii statystycznej –
          opiera się wyłącznie na danych czasowych dotyczących Polski. Dzięki
          temu, w przeciwieństwie do przekrojowych indeksów międzynarodowych,
          może on pełnić istotną rolę we wspieraniu zrównoważonego rozwoju
          krajowej gospodarki.
        </li>
      </ol>
      <p>
        Użytkownicy mają możliwość samodzielnego dostosowania stopnia wpływu
        kryteriów statystycznych na końcowy kształt rankingu, co zwiększa
        elastyczność i użyteczność narzędzia.
      </p>
      <h2 className="primary-header">Dostosuj ranking</h2>
      <div className="flex flex-col lg:flex-row w-full justify-start sm:justify-center items-center gap-8 lg:gap-20 mt-14 mb-20">
        <section className="text-center">
          <h3 className="-mt-2">Zakres rankingu</h3>
          <div className="join">
            <button
              className={clsx(
                'join-item btn btn-sm sm:btn-md lg:btn-lg rounded-full shadow-lg text-xs sm:text-sm lg:text-base',
                chosenRanking.scope === 'international'
                  ? 'btn-success'
                  : 'btn-inactive',
              )}
              onClick={() =>
                setChosenRanking((p) => ({ ...p, scope: 'international' }))
              }
            >
              Ranking międzynarodowy
            </button>
            <button
              className={clsx(
                'join-item btn btn-sm sm:btn-md lg:btn-lg rounded-full shadow-lg text-xs sm:text-sm lg:text-base',
                chosenRanking.scope === 'domestic'
                  ? 'btn-success'
                  : 'btn-inactive',
              )}
              onClick={() =>
                setChosenRanking((p) => ({ ...p, scope: 'domestic' }))
              }
            >
              <span className="hidden sm:inline">
                Polski Indeks Transformacji Energetycznej
              </span>
              <span className="sm:hidden">Polski ITE</span>
            </button>
          </div>
        </section>
        <section className="text-center">
          <h3 className="-mt-2">Typ rankingu</h3>
          <div className="join">
            <button
              className={clsx(
                'join-item btn btn-sm sm:btn-md lg:btn-lg rounded-full shadow-lg text-xs sm:text-sm lg:text-base',
                chosenRanking.mode === 'statistical'
                  ? 'btn-success'
                  : 'btn-inactive',
              )}
              onClick={() =>
                setChosenRanking((p) => ({ ...p, mode: 'statistical' }))
              }
            >
              Ranking statystyczny
            </button>
            <button
              className={clsx(
                'join-item btn btn-sm sm:btn-md lg:btn-lg rounded-full shadow-lg text-xs sm:text-sm lg:text-base',
                chosenRanking.mode === 'custom'
                  ? 'btn-success'
                  : 'btn-inactive',
              )}
              onClick={() =>
                setChosenRanking((p) => ({ ...p, mode: 'custom' }))
              }
            >
              Twój ranking
            </button>
          </div>
        </section>
      </div>
      {chosenRanking.mode == 'custom' && (
        <>
          <p className="-mt-8">
            Moduł "Twój ranking" pozwala modelować &nbsp;ranking zgodnie z
            indywidualnymi preferencjami. Mówiąc ściśle, można sprawdzić jak
            wygląda {chosenRanking.scope === 'domestic' ? 'PITE' : 'ITE'} po
            przypisaniu subiektywnych wag do poszczególnych kategorii
            tematycznych oraz po określeniu poziomu ich wpływu na końcowe
            wyniki.
          </p>
          <Indicators
            rankingScope={chosenRanking.scope}
            values={indicators}
            setValues={setIndicators}
          />
        </>
      )}
      <h2 className="primary-header">
        {chosenRanking.scope === 'domestic' ? 'PITE' : 'ZITE'}
      </h2>
      <RankingComponent
        yearsAvailable={internationalRankingYearsAvailable}
        urls={urls}
        csrfToken={csrfToken}
        indicators={indicators}
      />
    </div>
  );
}

function InternationalStatisticalRanking({ yearsAvailable, urls, csrfToken }) {
  const [activeYear, setActiveYear] = useState(
    yearsAvailable[yearsAvailable.length - 1],
  );
  const [plotData, setPlotData] = useState([]);

  useEffect(() => {
    fetch(
      urls['ite:international-statistical-report']() +
        `?max_number=30&year=${activeYear}`,
      {
        method: 'GET',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
      },
    )
      .then((res) => res.json())
      .then((json) => setPlotData(json));
  }, [activeYear]);

  return (
    <>
      <YearsTab
        years={yearsAvailable}
        activeYear={activeYear}
        setActiveYear={setActiveYear}
      />
      <PlotInternational data={plotData} />
      <p className="mt-4">
        Przedstawiony powyżej wykres pokazuje Indeks Transformacji Energetycznej
        (ITE) wyznaczony w oparciu o surowe dane statystyczne pobrane z bazy
        <strong> Światowego Forum Ekonomicznego</strong>. Dane te pozwalają
        wyznaczyć dwie rodziny wskaźników kompozytowych mierzące{' '}
        <strong>Sprawność Systemu</strong> (rodzina ta obejmuje wskaźniki
        mierzące trzy wymiary TE: Sprawiedliwość, Bezpieczeństwo i Zrównoważony
        rozwój) oraz <strong>Gotowość do Transformacji</strong> (rodzina
        obejmuje wskaźniki mierzące pięć wymiarów TE: Regulacja i system
        polityczny, Infrastruktura, Edukacja i kapitał ludzki, Innowacje,
        Finanse i inwestycje).
      </p>
      <p>
        W obliczeniach użyto wyłącznie wag wyznaczonych{' '}
        <strong>metodami statystycznymi</strong>, co zminimalizowało wpływ
        arbitralności. W przypadku każdej zmiennej większa wartość koresponduje
        z wyższą pozycją kraju w danym wymiarze.
      </p>
    </>
  );
}

function InternationalUserRanking({
  yearsAvailable,
  urls,
  csrfToken,
  indicators,
}) {
  const [activeYear, setActiveYear] = useState(
    yearsAvailable[yearsAvailable.length - 1],
  );
  const [plotData, setPlotData] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams({
      max_number: 30,
      year: activeYear,
      ...addDjangoFormPrefix(indicators),
    });
    fetch(urls['ite:international-user-report']() + `?${params.toString()}`, {
      method: 'GET',
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((json) => setPlotData(json));
  }, [activeYear, indicators]);

  return (
    <div>
      <YearsTab
        years={yearsAvailable}
        activeYear={activeYear}
        setActiveYear={setActiveYear}
      />
      <PlotInternational data={plotData} />
    </div>
  );
}

function PolandStatisticalRanking({ urls, csrfToken }) {
  const [plotData, setPlotData] = useState([]);

  useEffect(() => {
    fetch(urls['ite:poland-statistical-report']() + '?max_number=30', {
      method: 'GET',
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((json) => setPlotData(json));
  }, []);

  return (
    <div>
      <PlotPoland data={plotData} />
      <p className="mt-4">
        Przedstawiony powyżej wykres pokazuje Polski Indeks Transformacji
        {/* TODO - czy to 2015 - 2024 nie ma czasem być brane z danych? może się zmieniać? */}
        Energetycznej (PITE) wyznaczony dla lat 2015-2024 w oparciu o surowe
        dane statystyczne pobrane z bazy{' '}
        <strong>Światowego Forum Ekonomicznego</strong>, <strong>GUS</strong>,
        <strong> ARE</strong> oraz <strong>CBOS</strong>. Dane te pozwalają
        wyznaczyć dwie rodziny wskaźników kompozytowych mierzące{' '}
        <strong>Sprawność Systemu</strong> (rodzina ta obejmuje wskaźniki
        kompozytowe mierzące trzy wymiary TE: Sprawiedliwość, Bezpieczeństwo i
        Zrównoważony przebieg) oraz <strong>Gotowość do Transformacji</strong>{' '}
        (rodzina obejmuje wskaźniki kompozytowe mierzące pięć wymiarów TE:
        Regulacja i system polityczny, Infrastruktura, Edukacja i kapitał
        ludzki, Innowacje, Finanse i inwestycje).
      </p>
      <p>
        W obliczeniach użyto wyłącznie wag wyznaczonych{' '}
        <strong>metodami statystycznymi</strong>, co zminimalizowało wpływ
        arbitralności. W przypadku każdej zmiennej większa wartość koresponduje
        z wyższą pozycją kraju w danym wymiarze.
      </p>
    </div>
  );
}

// TODO: this can be simplified, perhaps thos ranking components can be removed,
//  and state pulled upwards
function PolandUserRanking({ urls, csrfToken, indicators }) {
  const [plotData, setPlotData] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(addDjangoFormPrefix(indicators));
    fetch(urls['ite:poland-user-report']() + `?${params.toString()}`, {
      method: 'GET',
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((json) => setPlotData(json));
  }, [indicators]);

  return (
    <div>
      <PlotPoland data={plotData} />
    </div>
  );
}

function PlotInternational({ data }) {
  const [recordsNo, setRecordsNo] = useState(10);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const handleBarClick = (payload) => {
    if (payload && payload.activePayload && payload.activePayload[0]) {
      setSelectedCountry(payload.activePayload[0].payload);
      setTimeout(() => {
        document.getElementById('mobile_chart_modal')?.showModal();
      }, 50);
    }
  };

  // NOTE: technically backend already does that work, but also
  //  we would have to make 3 separate requests to backend (each for
  //  each recordsNo) and if we do this in frontend, we make only 1
  //  (for the most records) and the just trim it when user selects less.
  const polandRecord = data.find((entry) => entry.kraj === 'Polska');
  const afterPolandRecords = data.filter((entry) => entry.is_after_poland);

  const ensureInSlice = (currentSlice, record) => {
    if (
      record &&
      !currentSlice.some((entry) => entry && entry.kraj === record.kraj)
    ) {
      return [...currentSlice, record];
    }
    return currentSlice;
  };

  let slicedData = data.slice(0, recordsNo);
  slicedData = ensureInSlice(slicedData, polandRecord);
  slicedData = afterPolandRecords.reduce(ensureInSlice, slicedData);

  slicedData = slicedData
    .filter(Boolean)
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));

  const chartHeight = Math.max(slicedData.length, 1) * 40;

  const getCellClass = (entry) => {
    if (entry.kraj === 'Polska') return 'fill-info';
    if (entry.is_after_poland) return 'fill-primary';
    return 'fill-secondary';
  };

  const maxWidthLabel = data.reduce(
    (max, entry) => (entry.kraj.length > max.kraj.length ? entry : max),
    { kraj: '' },
  ).kraj;

  return (
    <>
      {data.length > 0 ? (
        <div className="w-full">
          <div className="flex justify-between my-10 max-w-none mx-4 sm:mx-8 gap-2 md:justify-evenly md:gap-4">
            <button
              className={clsx(
                'btn btn-sm sm:btn-md rounded-full shadow-md text-xs sm:text-sm flex-1 md:flex-initial md:px-8',
                recordsNo === 10 ? 'btn-success' : 'btn-inactive',
              )}
              onClick={() => setRecordsNo(10)}
            >
              top 10
            </button>
            <button
              className={clsx(
                'btn btn-sm sm:btn-md rounded-full shadow-md text-xs sm:text-sm flex-1 md:flex-initial md:px-8',
                recordsNo === 20 ? 'btn-success' : 'btn-inactive',
              )}
              onClick={() => setRecordsNo(20)}
            >
              top 20
            </button>
            <button
              className={clsx(
                'btn btn-sm sm:btn-md rounded-full shadow-md text-xs sm:text-sm flex-1 md:flex-initial md:px-8',
                recordsNo === 30 ? 'btn-success' : 'btn-inactive',
              )}
              onClick={() => setRecordsNo(30)}
            >
              top 30
            </button>
          </div>
          <div className="mx-4">
            <div className="md:hidden">
              <ResponsiveContainer width={'100%'} height={chartHeight}>
                <BarChart
                  data={slicedData}
                  margin={{
                    top: 5,
                    bottom: 5,
                    left: 10,
                    right: 10,
                  }}
                  layout="vertical"
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="kraj"
                    type="category"
                    hide
                    tick={false}
                    width={0}
                  />
                  <Bar dataKey="METI" cursor="pointer">
                    <LabelList
                      dataKey="kraj"
                      position="insideLeft"
                      content={<MobileCountryRankLabel />}
                    />
                    {slicedData.map((entry, index) => (
                      <Cell
                        key={`mobile-cell-${index}`}
                        className={getCellClass(entry)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="hidden md:block">
              <ResponsiveContainer width={'100%'} height={chartHeight}>
                <BarChart
                  data={slicedData}
                  margin={{
                    top: 5,
                    bottom: 5,
                    left: 10,
                    right: 10,
                  }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="kraj"
                    type="category"
                    tick={{ style: { whiteSpace: 'nowrap' } }}
                    width={Math.min(maxWidthLabel.length * 8, 200)}
                  />
                  <Tooltip content={<CustomTooltip withRank />} />
                  <Bar dataKey="METI" cursor="default">
                    {slicedData.map((entry, index) => (
                      <Cell
                        key={`desktop-cell-${index}`}
                        className={getCellClass(entry)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mx-4 mt-6 text-sm text-base-content/70">
            {CHART_LEGEND_ITEMS.map(({ colorClass, label }) => (
              <span key={label} className="flex items-center gap-2">
                <span
                  className={clsx('h-3 w-3 rounded-full', colorClass)}
                  aria-hidden="true"
                />
                <span>{label}</span>
              </span>
            ))}
          </div>

          <div className="md:hidden">
            {selectedCountry && (
              <MobileChartModal
                selectedCountry={selectedCountry}
                onClose={() => setSelectedCountry(null)}
              />
            )}
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
}

function PlotPoland({ data }) {
  const [selectedYear, setSelectedYear] = useState(null);

  const handleBarClick = (payload) => {
    if (payload && payload.activePayload && payload.activePayload[0]) {
      setSelectedYear(payload.activePayload[0].payload);
      setTimeout(() => {
        document.getElementById('mobile_poland_chart_modal')?.showModal();
      }, 50);
    }
  };

  return (
    <div className="mt-14 w-full">
      {data.length > 0 ? (
        <>
          <div className="mx-4 sm:mx-8">
            <div className="md:hidden">
              <ResponsiveContainer width={'100%'} height={500}>
                <BarChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 10,
                    left: 10,
                    bottom: 5,
                  }}
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rok" />
                  <YAxis />
                  <Bar
                    dataKey="METI"
                    className="fill-secondary"
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="hidden md:block">
              <ResponsiveContainer width={'100%'} height={500}>
                <BarChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 10,
                    left: 10,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rok" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="METI"
                    className="fill-secondary"
                    cursor="default"
                  >
                    <LabelList
                      dataKey="METI"
                      position="top"
                      className="font-semibold"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="md:hidden">
            {selectedYear && (
              <MobilePolandChartModal
                selectedYear={selectedYear}
                onClose={() => setSelectedYear(null)}
              />
            )}
          </div>
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
}

function Indicators({ rankingScope, values, setValues }) {
  const [activeTab, setActiveTab] = useState('performance'); // 'performance' or 'transformation'

  const handleRangeChange = (e) => {
    const { name, value } = e.target;
    setValues((p) => ({
      ...p,
      [name]: Number(value),
    }));
  };

  return (
    <div className="flex flex-col gap-12 mt-4 mb-5">
      <section>
        <h3 className="mt-0">Znaczenie preferowanych kryteriów</h3>
        <div className="flex flex-col items-center mt-4 space-y-4 md:hidden">
          <RangeLabel
            label="Stopień znaczenia ocen arbitralnych"
            name="arbitrary_assessment"
            onChange={(e) => handleRangeChange(e)}
            defaultValue={values['arbitrary_assessment']}
            className={'range-primary w-full'}
            labelClassName={'text-center'}
            min={1}
            max={5}
          />
          <ChevronRight className={'h-12 w-16 rotate-90 text-primary'} />
          <div className="text-center px-2">
            Określ jak duże znaczenie w końcowym kształcie{' '}
            {rankingScope === 'domestic' ? 'PITE' : 'ZITE'} mają mieć
            preferowane przez Ciebie kryteria. Przesunięcie suwaka maksymalnie w
            lewo oznacza, że {rankingScope === 'domestic' ? 'PITE' : 'ZITE'}{' '}
            zostanie oparty wyłącznie na wagach wynikających z cech danych
            statystycznych. Przesuwanie suwaka w prawo zwiększa wpływ
            subiektywnych kryteriów użytkownika na końcowy ranking.
          </div>
        </div>
        <div className="hidden md:flex gap-8 items-center mt-4">
          <RangeLabel
            label="Stopień znaczenia ocen arbitralnych"
            name="arbitrary_assessment"
            onChange={(e) => handleRangeChange(e)}
            defaultValue={values['arbitrary_assessment']}
            className={'min-w-96 range-primary'}
            labelClassName={'-mt-3'}
            min={0}
            max={5}
          />
          <ChevronRight className={'h-14 w-20 -mr-4 rotate-180'} />
          Określ jak duże znaczenie w końcowym kształcie{' '}
          {rankingScope === 'domestic' ? 'PITE' : 'ZITE'} mają mieć preferowane
          przez Ciebie kryteria. Przesunięcie suwaka maksymalnie w lewo oznacza,
          że {rankingScope === 'domestic' ? 'PITE' : 'ZITE'} zostanie oparty
          wyłącznie na wagach wynikających z cech danych statystycznych.
          Przesuwanie suwaka w prawo zwiększa wpływ subiektywnych kryteriów
          użytkownika na końcowy ranking.
        </div>
      </section>
      <section className="-mt-8">
        <h3 className="mt-0">Kryteria</h3>
        <div>
          <p id="indicators">
            Wskaż, które kryteria mają dla Ciebie kluczowe znaczenie.
            Przesuwanie suwaka w prawo (lewo) zwiększa (zmniejsza) znaczenie
            danego kryterium w obliczonej wartości{' '}
            {rankingScope === 'domestic' ? 'PITE' : 'ZITE'}. <br />
            <span className="text-error">Uwaga!</span>{' '}
            <span className="underline">
              W zależności od wybranego zakresu rankingu, obejmowane przez
              kryterium wskaźniki składowe mogą się różnić.
            </span>
          </p>
          <div className="md:hidden">
            <div
              role="tablist"
              className="tabs tabs-lg tabs-bordered justify-stretch mt-8 mb-6"
            >
              <button
                role="tab"
                className={clsx(
                  'tab no-underline flex-1',
                  activeTab === 'performance' && 'tab-active',
                )}
                onClick={() => setActiveTab('performance')}
              >
                Wydajność
              </button>
              <button
                role="tab"
                className={clsx(
                  'tab no-underline flex-1',
                  activeTab === 'transformation' && 'tab-active',
                )}
                onClick={() => setActiveTab('transformation')}
              >
                Transformacja
              </button>
            </div>

            {activeTab === 'performance' && (
              <section className="space-y-6">
                <h4 className="text-center font-medium text-success">
                  Sprawność systemu
                </h4>
                <RangeLabel
                  label={INDICATORS_INFO.equitable.label}
                  name="equitable"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['equitable']}
                  className={'range-success'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'equitable',
                  )}
                />
                <RangeLabel
                  label={INDICATORS_INFO.secure.label}
                  name="secure"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['secure']}
                  className={'range-success'}
                  description={getIndicatorDescription(rankingScope, 'secure')}
                />
                <RangeLabel
                  label={INDICATORS_INFO.sustainable.label}
                  name="sustainable"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['sustainable']}
                  className={'range-success'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'sustainable',
                  )}
                />
              </section>
            )}

            {activeTab === 'transformation' && (
              <section className="space-y-6">
                <h4 className="text-center font-medium text-secondary">
                  Gotowość do transformacji
                </h4>
                <RangeLabel
                  label={INDICATORS_INFO.regulation.label}
                  name="regulation"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['regulation']}
                  className={'range-secondary'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'regulation',
                  )}
                />
                <RangeLabel
                  label={INDICATORS_INFO.infrastructure.label}
                  name="infrastructure"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['infrastructure']}
                  className={'range-secondary'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'infrastructure',
                  )}
                />
                <RangeLabel
                  label={INDICATORS_INFO.education.label}
                  name="education"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['education']}
                  className={'range-secondary'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'education',
                  )}
                />
                <RangeLabel
                  label={INDICATORS_INFO.innovation.label}
                  name="innovation"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['innovation']}
                  className={'range-secondary'}
                  description={getIndicatorDescription(
                    rankingScope,
                    'innovation',
                  )}
                />
                <RangeLabel
                  label={INDICATORS_INFO.finance.label}
                  name="finance"
                  onChange={(e) => handleRangeChange(e)}
                  defaultValue={values['finance']}
                  className={'range-secondary'}
                  description={getIndicatorDescription(rankingScope, 'finance')}
                />
              </section>
            )}
          </div>
          <div className="hidden md:block">
            <div className="divider mt-8">Sprawność systemu</div>
            <section className="flex items-center gap-14 justify-between">
              <RangeLabel
                label={INDICATORS_INFO.equitable.label}
                name="equitable"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['equitable']}
                className={'range-success'}
                description={getIndicatorDescription(rankingScope, 'equitable')}
              />
              <RangeLabel
                label={INDICATORS_INFO.secure.label}
                name="secure"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['secure']}
                className={'range-success'}
                description={getIndicatorDescription(rankingScope, 'secure')}
              />
              <RangeLabel
                label={INDICATORS_INFO.sustainable.label}
                name="sustainable"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['sustainable']}
                className={'range-success'}
                description={getIndicatorDescription(
                  rankingScope,
                  'sustainable',
                )}
              />
            </section>
            <div className="divider mt-12">Gotowość do transformacji</div>
            <section className="grid grid-rows-2 grid-cols-3 gap-x-14 gap-y-4">
              <RangeLabel
                label={INDICATORS_INFO.regulation.label}
                name="regulation"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['regulation']}
                className={'range-secondary'}
                description={getIndicatorDescription(
                  rankingScope,
                  'regulation',
                )}
              />
              <RangeLabel
                label={INDICATORS_INFO.infrastructure.label}
                name="infrastructure"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['infrastructure']}
                className={'range-secondary'}
                description={getIndicatorDescription(
                  rankingScope,
                  'infrastructure',
                )}
              />
              <RangeLabel
                label={INDICATORS_INFO.education.label}
                name="education"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['education']}
                className={'range-secondary'}
                description={getIndicatorDescription(rankingScope, 'education')}
              />
              <RangeLabel
                label={INDICATORS_INFO.innovation.label}
                name="innovation"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['innovation']}
                className={'range-secondary'}
                description={getIndicatorDescription(
                  rankingScope,
                  'innovation',
                )}
              />
              <RangeLabel
                label={INDICATORS_INFO.finance.label}
                name="finance"
                onChange={(e) => handleRangeChange(e)}
                defaultValue={values['finance']}
                className={'range-secondary'}
                description={getIndicatorDescription(rankingScope, 'finance')}
              />
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}

function RangeLabel({
  label,
  name,
  onChange,
  defaultValue,
  className,
  labelClassName,
  min = 1,
  max = 5,
  description,
}) {
  const renderMarks = () => {
    const marks = [];
    for (let i = min; i <= max; i++) {
      marks.push(<span key={i}>{i}</span>);
    }
    return marks;
  };

  return (
    <label
      className={twMerge(
        'mb-2 font-semibold text-start basis-0 flex-grow flex-shrink flex flex-col gap-2',
        labelClassName,
      )}
    >
      <span className="leading-tight min-h-[2.75rem] sm:min-h-[3rem] md:min-h-[3.25rem] flex items-end">
        {label}
        {description && (
          <Tippy content={description} placement="top" theme="custom-dark">
            <span className="ml-2 cursor-pointer inline-flex items-center">
              <Info className="stroke-current w-5 h-5" />
            </span>
          </Tippy>
        )}
      </span>
      <div className="w-full">
        <input
          type="range"
          min={min}
          max={max}
          defaultValue={defaultValue}
          className={twMerge('range range-lg', className)}
          name={name}
          onChange={onChange}
        />
        <div className="flex justify-between px-2.5 mt-2 text-xs">
          {renderMarks()}
        </div>
      </div>
    </label>
  );
}

function YearsTab({ years, activeYear, setActiveYear }) {
  return (
    <div
      role="tablist"
      className="tabs tabs-lg tabs-bordered justify-stretch mt-6"
    >
      {years
        .sort((a, b) => a - b)
        .map((year) => (
          <a
            role="tab"
            className={clsx(
              'tab no-underline',
              activeYear === year && 'tab-active',
            )}
            onClick={() => setActiveYear(year)}
          >
            {year}
          </a>
        ))}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, withRank }) => {
  const polishTranslations = {
    Equitable: 'Sprawiedliwość',
    Secure: 'Bezpieczeństwo',
    Sustainable: 'Zrównoważony rozwój',
    'Regulation and political commitment': 'Regulacja i system polityczny',
    Infrastructure: 'Infrastruktura',
    'Education and human capital': 'Edukacja i kapitał ludzki',
    Innovation: 'Innowacje',
    'Finance and investment': 'Finanse i inwestycje',
  };
  const iconsMapping = {
    Sprawiedliwość: EquitableIcon,
    Bezpieczeństwo: SecureIcon,
    'Zrównoważony rozwój': SustainableIcon,
    'Regulacja i system polityczny': RegulationIcon,
    Infrastruktura: InfrastructureIcon,
    'Edukacja i kapitał ludzki': EducationIcon,
    Innowacje: InnovationIcon,
    'Finanse i inwestycje': FinanceIcon,
  };

  if (active && payload && payload.length) {
    const subValues = { ...payload[0].payload };
    const rank = subValues.rank;
    delete subValues.rank;
    delete subValues.kraj;
    delete subValues.METI;
    delete subValues.index;
    delete subValues.rok;
    delete subValues.is_poland;
    delete subValues.is_after_poland;

    // Prepare data for the horizontal bar chart
    const chartData = Object.keys(subValues).map((key) => ({
      name: polishTranslations[key] || key,
      value: subValues[key],
    }));

    // Find the maximum value for scaling the bars
    const maxValue = Math.max(...chartData.map((d) => d.value), 1);

    return (
      <div className="bg-white prose p-4 rounded shadow-lg min-w-[500px]">
        <h3 className="text-primary mb-2 flex justify-between">
          <span>
            {label} - {payload[0].value}
          </span>
          {rank && withRank && (
            <span className="ml-auto">(miejsce {rank})</span>
          )}
        </h3>
        <ResponsiveContainer width={'100%'} height={chartData.length * 32 + 30}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: -30, bottom: 10 }}
          >
            <XAxis type="number" hide domain={[0, maxValue]} />
            <YAxis
              dataKey="name"
              type="category"
              tick={<CustomTick iconsMapping={iconsMapping} />}
              axisLine={false}
              tickLine={false}
              width={280}
            />
            <Bar dataKey="value" className="fill-success" maxBarSize={12}>
              <LabelList
                dataKey="value"
                position="right"
                formatter={(v) => Number(v).toFixed(2)}
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              />
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  className={index > 2 ? 'fill-secondary' : 'fill-success'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
};

const CustomTick = ({ x, y, payload, iconsMapping }) => {
  // NOTE: uncomment if prefering to render some ticks in multiline
  // const multilinePayloads = ["Regulacje i zaangażowanie polityczne"];
  // const isMultilineTick = multilinePayloads.includes(payload.value);
  const isMultilineTick = false;
  const translateX = isMultilineTick ? x - 180 : x - 240;
  const translateY = isMultilineTick ? y - 20 : y - 12;
  const width = isMultilineTick ? 180 : 260;
  const height = isMultilineTick ? 30 : 30;

  console.log('pl', payload);
  const Icon = iconsMapping[payload.value];

  return (
    <g transform={`translate(${translateX},${translateY})`}>
      {/* <foreignObject x={0} y={0} width={60} height={30}> */}
      <foreignObject x={0} y={0} width={width} height={height}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            fontSize: 12,
            gap: 8,
          }}
        >
          <span className="flex w-4 h-4 items-center justify-center">
            {Icon && <Icon />}
          </span>
          <span style={{ textAlign: 'end' }}>{payload.value}</span>
        </div>
      </foreignObject>
    </g>
  );
};

const MobileCountryRankLabel = (props) => {
  const { x, y, height, value, payload } = props;
  if (!value) {
    return null;
  }

  const rank = payload?.rank;
  const label = rank ? `${rank}. ${value}` : value;

  return (
    <text
      x={(x ?? 0) + 8}
      y={(y ?? 0) + (height ?? 0) / 2}
      fill="white"
      fontSize={12}
      fontWeight={600}
      dominantBaseline="middle"
      textAnchor="start"
      pointerEvents="none"
    >
      {label}
    </text>
  );
};

function MobileChartModal({ selectedCountry, onClose }) {
  if (!selectedCountry) return null;

  const polishTranslations = {
    Equitable: 'Sprawiedliwość',
    Secure: 'Bezpieczeństwo',
    Sustainable: 'Zrównoważony rozwój',
    'Regulation and political commitment': 'Regulacja i system polityczny',
    Infrastructure: 'Infrastruktura',
    'Education and human capital': 'Edukacja i kapitał ludzki',
    Innovation: 'Innowacje',
    'Finance and investment': 'Finanse i inwestycje',
  };

  const iconsMapping = {
    Sprawiedliwość: EquitableIcon,
    Bezpieczeństwo: SecureIcon,
    'Zrównoważony rozwój': SustainableIcon,
    'Regulacja i system polityczny': RegulationIcon,
    Infrastruktura: InfrastructureIcon,
    'Edukacja i kapitał ludzki': EducationIcon,
    Innowacje: InnovationIcon,
    'Finanse i inwestycje': FinanceIcon,
  };

  // Use the same logic as CustomTooltip
  const subValues = { ...selectedCountry };
  delete subValues.kraj;
  delete subValues.METI;
  delete subValues.index;
  delete subValues.rok;
  delete subValues.is_poland;
  delete subValues.is_after_poland;
  delete subValues.rank;

  // Prepare data for the chart
  const chartData = Object.keys(subValues).map((key) => ({
    name: polishTranslations[key] || key,
    value: subValues[key],
    icon: iconsMapping[polishTranslations[key] || key],
  }));

  // Find the maximum value for scaling
  const maxValue = Math.max(...chartData.map((d) => d.value), 1);

  return (
    <dialog
      id="mobile_chart_modal"
      className="modal modal-bottom focus:outline-none"
      style={{ outline: 'none' }}
    >
      <div
        className="modal-box rounded-t-lg rounded-b-none max-h-[80vh] focus:outline-none"
        style={{ outline: 'none' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">
            {selectedCountry.kraj}
            {selectedCountry.rank && (
              <span className="ml-2 text-sm font-normal text-base-content/70">
                (miejsce {selectedCountry.rank})
              </span>
            )}
          </h2>
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost"
              onClick={onClose}
            >
              ✕
            </button>
          </form>
        </div>

        <div className="mb-4">
          <div className="stat bg-base-200 rounded-lg p-4 mb-4">
            <div className="stat-title text-sm">Wartość METI</div>
            <div className="stat-value text-primary text-2xl">
              {Number(selectedCountry.METI).toFixed(5)}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold text-base mb-3">Rozkład wskaźników:</h3>
          {chartData.map((item, index) => {
            const Icon = item.icon;
            const percentage = (item.value / maxValue) * 100;

            return (
              <div key={index} className="flex items-center space-x-3 p-2">
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {Icon && <Icon className="w-5 h-5" />}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium truncate pr-2">
                      {item.name}
                    </span>
                    <span className="text-sm font-semibold">
                      {Number(item.value).toFixed(2)}
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        index > 2 ? 'bg-secondary' : 'bg-success'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-action pt-4">
          <form method="dialog">
            <button className="btn btn-primary w-full" onClick={onClose}>
              Zamknij
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );
}

function MobilePolandChartModal({ selectedYear, onClose }) {
  if (!selectedYear) return null;

  const polishTranslations = {
    Equitable: 'Sprawiedliwość',
    Secure: 'Bezpieczeństwo',
    Sustainable: 'Zrównoważony rozwój',
    'Regulation and political commitment': 'Regulacja i system polityczny',
    Infrastructure: 'Infrastruktura',
    'Education and human capital': 'Edukacja i kapitał ludzki',
    Innovation: 'Innowacje',
    'Finance and investment': 'Finanse i inwestycje',
  };

  const iconsMapping = {
    Sprawiedliwość: EquitableIcon,
    Bezpieczeństwo: SecureIcon,
    'Zrównoważony rozwój': SustainableIcon,
    'Regulacja i system polityczny': RegulationIcon,
    Infrastruktura: InfrastructureIcon,
    'Edukacja i kapitał ludzki': EducationIcon,
    Innowacje: InnovationIcon,
    'Finanse i inwestycje': FinanceIcon,
  };

  // Use the same logic as CustomTooltip
  const subValues = { ...selectedYear };
  delete subValues.kraj;
  delete subValues.METI;
  delete subValues.index;
  delete subValues.rok;

  // Prepare data for the chart
  const chartData = Object.keys(subValues).map((key) => ({
    name: polishTranslations[key] || key,
    value: subValues[key],
    icon: iconsMapping[polishTranslations[key] || key],
  }));

  // Find the maximum value for scaling
  const maxValue = Math.max(...chartData.map((d) => d.value), 1);

  return (
    <dialog
      id="mobile_poland_chart_modal"
      className="modal modal-bottom focus:outline-none"
      style={{ outline: 'none' }}
    >
      <div
        className="modal-box rounded-t-lg rounded-b-none max-h-[80vh] focus:outline-none"
        style={{ outline: 'none' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">Rok {selectedYear.rok}</h2>
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost"
              onClick={onClose}
            >
              ✕
            </button>
          </form>
        </div>

        <div className="mb-4">
          <div className="stat bg-base-200 rounded-lg p-4 mb-4">
            <div className="stat-title text-sm">Wartość PITE</div>
            <div className="stat-value text-primary text-2xl">
              {Number(selectedYear.METI).toFixed(5)}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold text-base mb-3">Rozkład wskaźników:</h3>
          {chartData.map((item, index) => {
            const Icon = item.icon;
            const percentage = (item.value / maxValue) * 100;

            return (
              <div key={index} className="flex items-center space-x-3 p-2">
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {Icon && <Icon className="w-5 h-5" />}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium truncate pr-2">
                      {item.name}
                    </span>
                    <span className="text-sm font-semibold">
                      {Number(item.value).toFixed(2)}
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        index > 2 ? 'bg-secondary' : 'bg-success'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-action pt-4">
          <form method="dialog">
            <button className="btn btn-primary w-full" onClick={onClose}>
              Zamknij
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );
}

function TutorialBtn({ className }) {
  return (
    <a
      href="https://www.youtube.com/watch?v=pkcTe_df5e8&list=PLsr7358AKbHrVSgDYDyxCuae8mXe5Risx&index=3"
      target="_blank"
      className={twMerge(
        'btn btn-outline btn-sm gap-2 whitespace-nowrap flex-shrink-0 shadow shadow-white ml-10 text-white hover:bg-white hover:text-black absolute right-10 hidden lg:inline-flex',
        className,
      )}
      rel="noopener noreferrer"
      title="Zobacz samouczek video"
    >
      <span
        className="hidden"
        dangerouslySetInnerHTML={{
          __html:
            '<!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->',
        }}
      ></span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 576 512"
        className="h-4 w-4"
        fill="currentColor"
      >
        <path d="M0 128C0 92.7 28.7 64 64 64l256 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2l0 256c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1l0-17.1 0-128 0-17.1 14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z" />
      </svg>
      Samouczek
    </a>
  );
}

/**
 * input:
 *   { 'param1': 'value1', 'param2': 'value2'}
 * output:
 *   { 'up-param1': 'value1', 'up-param2': 'value2'}
 */
function addDjangoFormPrefix(params) {
  const djangoFormPrefix = 'up-'; // "up" as in "User Preferences"
  const paramsWithPrefix = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      `${djangoFormPrefix}${key}`,
      value,
    ]),
  );
  return paramsWithPrefix;
}

function getIndicatorDescription(rankingScope, indicator) {
  const info = INDICATORS_INFO[indicator];
  return rankingScope == 'domestic'
    ? info.descriptionPite
    : info.descriptionZite;
}
